import dayjs from "dayjs"
import type {
  MutableRefObject,
  RefObject,
} from "react"
import { createContext } from "react"
import type { VariableSizeList } from "react-window"

import type { ZoomLevel } from "./useGraphZoom"
import type {
  ReactWindowAutoSizerArgs,
  ReactWindowOnScrollArgs,
} from "src/types"

export interface GraphContextValue {
  graphEnd: string; // the right edge of the graph when viewed left-to-right oldest-to-newest
  graphStart: string; // the left edge of the graph when viewed left-to-right oldest-to-newest
  pointsPerHour: number; // represents the width of an hour along the x-axis
  hoursPerDivider: number; // represents the duration of time between the markings on the x-axis
  dividersPerTimeLabel: number; // represents the number of dividers per time label
  hoursPerGraphItem: number; // represents the duration of time covered by each item of the FlashList
  numberOfItems: number; // the number of items in the FlashList
  listRef: RefObject<VariableSizeList | null>; // reference to the element that is the list
  onScroll: (e: ReactWindowOnScrollArgs) => void; // called when the FlashList scrolls
  onResize: (e: ReactWindowAutoSizerArgs) => void; // called when the FlashList layout changes
  topMgdl: number; // graph y-axis max
  bottomMgdl: number; // graph y-axis min
  yAxisHeight: number; // height of the y-axis
  graphActivityHeight: number; // the height of non-glucose items
  graphIconSize: number; // the height and width of graph activity icons
  zoom: ZoomLevel; // sets the number of hours visible in graph window
  setZoom: (v: ZoomLevel) => void; // updates the number of hours visible in graph window
  graphScrollDate: Date;
  scrollPositionRef: MutableRefObject<number>; // the current scroll position of the graph
}

export const GraphContext = createContext<GraphContextValue>({
  graphEnd: dayjs().add(
    1,
    "hours",
  )
    .toISOString(),
  graphStart: dayjs().startOf("day")
    .subtract(
      0,
      "days",
    )
    .toISOString(),
  pointsPerHour: 96,
  hoursPerDivider: 1,
  dividersPerTimeLabel: 1,
  hoursPerGraphItem: 24,
  numberOfItems: 91,
  onScroll: () => {},
  onResize: () => {},
  topMgdl: 400,
  bottomMgdl: 0,
  yAxisHeight: 224,
  listRef: { current: null },
  graphActivityHeight: 24,
  graphIconSize: 16,
  zoom: 3,
  setZoom: () => {},
  graphScrollDate: dayjs().toDate(),
  scrollPositionRef: { current: 0 },
})
